import type { AmrapStop, EffortScale } from '@heraion/shared';
import type { TFn } from '../i18nTypes';
import { amrapStopSentence, amrapStopShort } from '../amrapStopWords';

// Structural t-signature — the repo has no direct i18next dep to import
// TFunction from; every caller passes useTranslation()'s t. Matches the
// convention already used by patternLabel.ts / difficultyLabel.ts.
type Translate = (key: string, opts?: { defaultValue?: string }) => string;

export type EffortWording = {
  shownScale: EffortScale;
  questionText: string;
  effortWord: string;
  amrapWord: string;
  doneWord: string;
  /** What the collapsed band says to a screen reader, when it is not `doneWord`. */
  doneA11y?: string;
  isAmrap: boolean;
  shortPrompt: boolean;
};

/** The wording the effort band shows, derived from the set + the athlete's
 *  scale. Pure: no hooks, no state — booleans and t() only. Extracted from
 *  FocusPanelEffort.tsx (SP-FOCUSPANELEFFORT, 2026-07-27) to get that file
 *  under the 500-line cap. The rationale comments below moved with the code
 *  they explain; they are the reason these collapses exist and are easy to
 *  get wrong from the outside. */
export function deriveEffortWording({
  isDuration,
  effortScale,
  amrap,
  stop,
  t,
}: {
  isDuration: boolean;
  effortScale: EffortScale;
  amrap: boolean;
  /** The program's AMRAP stop rule when it is technical failure (#355). */
  stop?: Extract<AmrapStop, { kind: 'technical-failure' }>;
  t: Translate;
}): EffortWording {
  // The athlete-facing prompt — RIR asks what's left in the tank, RPE asks how
  // hard it felt (same effort read from opposite ends). One string for both the
  // visible question and its a11y label.
  // A duration set reads the band as RPE whatever the athlete's own scale (D1):
  // on a hold there are no reps left in the tank to count, so the RIR question
  // has no answer. The canonical value written is unaffected — this is print.
  const shownScale: EffortScale = isDuration ? 'rpe' : effortScale;
  const questionText =
    shownScale === 'rir'
      ? t('mobile.logger.effortQuestionRir', {
          defaultValue: 'Reps in reserve?',
        })
      : t('mobile.logger.effortQuestionRpe', {
          defaultValue: 'How hard was it?',
        });
  // The compact variant says the one word instead of the whole question — it
  // has 76pt on the axis, not 244 (D11). The full question stays the spoken
  // label, so a screen reader still hears what's being asked.
  const effortWord = t('mobile.logger.effortWord', { defaultValue: 'Effort' });
  // AMRAP smoke (Antenore 2026-07-23): the collapsed band's axis says "AMRAP",
  // not "Effort" — the set IS the test, and the word names it. The duration
  // variant keeps "Effort". `amrapBadge` is the same word already shown on the
  // prescribed badge, in all 5 locales.
  const amrapWord = t('program.amrapBadge', { defaultValue: 'AMRAP' });
  const plainDone = t('mobile.logger.effortMeterAmrapDone', {
    defaultValue: 'Done',
  });
  // #355: with a "form breaks" rule the band prints the rule's own short word
  // (amrapStopWords, the editor's chip): the segment is one line, so «Done ·
  // …» would truncate at a large font. The sentence, with «Done», is spoken.
  // It still writes RIR 0, which is what that rule means.
  const doneWord = stop ? amrapStopShort(t as TFn, stop) : plainDone;
  const doneA11y = stop
    ? `${plainDone}, ${amrapStopSentence(t as TFn, stop)}`
    : undefined;
  // The AMRAP OUTCOME the athlete declared with the chip — never
  // `amrapPrescribed`, which is only what the program asked for and stays
  // display-only (confusing the two is the defect this slice already fixed
  // once). `!isDuration` is a belt on a stale flag: the chip is offered on reps
  // sets only, and the compact variant already answers the same question there
  // — the two collapses must never stack.
  const isAmrap = amrap === true && !isDuration;
  // The axis asks the band's question, so it collapses with it: where the
  // question has one known answer, the axis says the single word the compact
  // variant already says where it has none. Reused verbatim, in all 5 locales
  // already — the AMRAP explanation is the chip's tip, not this row's job.
  const shortPrompt = isDuration || isAmrap;

  return {
    shownScale,
    questionText,
    effortWord,
    amrapWord,
    doneWord,
    ...(doneA11y ? { doneA11y } : {}),
    isAmrap,
    shortPrompt,
  };
}
